import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { detectUserRegion } from "@/lib/geolocation";
import { Instagram, Facebook, Youtube } from "lucide-react";
import { trackSocialClick } from "@/lib/analytics";
import threadsIcon from "@/assets/threads-icon.png";

const PinterestIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0a12 12 0 0 0-4.37 23.17c-.1-.94-.2-2.4.04-3.43.22-.93 1.4-5.94 1.4-5.94s-.36-.72-.36-1.78c0-1.67.97-2.92 2.17-2.92 1.03 0 1.52.77 1.52 1.7 0 1.03-.66 2.58-1 4.02-.28 1.2.6 2.18 1.79 2.18 2.15 0 3.8-2.27 3.8-5.54 0-2.9-2.08-4.92-5.05-4.92-3.44 0-5.46 2.58-5.46 5.25 0 1.04.4 2.16.9 2.76.1.12.11.23.08.35-.09.36-.28 1.2-.32 1.36-.05.22-.17.27-.4.16-1.5-.7-2.43-2.88-2.43-4.64 0-3.78 2.75-7.25 7.92-7.25 4.16 0 7.39 2.96 7.39 6.92 0 4.13-2.6 7.46-6.22 7.46-1.21 0-2.36-.63-2.75-1.38l-.75 2.85c-.27 1.04-1 2.35-1.49 3.15A12 12 0 1 0 12 0z" />
  </svg>
);

const linkCls =
  "text-[14px] text-[#555] hover:text-[#111] transition-colors inline-block";

const colHeadCls =
  "text-[11px] font-semibold tracking-[0.14em] uppercase text-[#111] mb-5";

export const Footer = () => {
  const [showCCPA, setShowCCPA] = useState(false);

  useEffect(() => {
    detectUserRegion().then(({ isCalifornia }) => {
      setShowCCPA(isCalifornia);
    });
  }, []);

  return (
    <footer className="footer-editorial bg-white border-t border-[#e8e8e8] mt-auto">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-6 lg:px-10 pt-12 sm:pt-16 pb-10 lg:pt-20 lg:pb-12">
        <div className="grid grid-cols-12 gap-8 sm:gap-10 lg:gap-14">
          {/* Brand */}
          <div className="col-span-12 lg:col-span-4">
            <Link to="/" className="inline-flex items-center" aria-label="Decor Canvas — Home">
              <span className="text-[20px] font-semibold tracking-[-0.02em] text-[#111]">
                Decor Canvas
              </span>
            </Link>
            <div className="mt-3 text-[11px] tracking-[0.14em] uppercase text-[#888]">
              Interior Decor AI Designer
            </div>
            <p className="mt-5 max-w-none sm:max-w-[360px] text-[15px] leading-[1.65] text-[#555]">
              Transform any room with AI-powered interior design. Visualize before you renovate —
              on web, iOS and Android.
            </p>

            <div className="mt-7 flex items-center gap-4">
              {[
                { href: "https://www.instagram.com/decorcanvas2026", label: "Instagram", key: "instagram" as const, node: <Instagram className="h-4 w-4" /> },
                { href: "https://www.facebook.com/profile.php?id=61587999902768", label: "Facebook", key: "facebook" as const, node: <Facebook className="h-4 w-4" /> },
                { href: "https://www.youtube.com/@decorcanvas", label: "YouTube", key: "youtube" as const, node: <Youtube className="h-4 w-4" /> },
                { href: "https://www.threads.com/@decorcanvas2026", label: "Threads", key: "threads" as const, node: <img src={threadsIcon} alt="Threads" className="h-4 w-4 opacity-70" /> },
                { href: "https://uk.pinterest.com/DecorCanvas/", label: "Pinterest", key: "pinterest" as const, node: <PinterestIcon /> },
              ].map((s) => (
                <a
                  key={s.key}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  onClick={() => trackSocialClick(s.key, s.href)}
                  className="w-9 h-9 rounded-full border border-[#e8e8e8] flex items-center justify-center text-[#555] hover:text-[color:var(--ed-coral)] hover:border-[color:var(--ed-coral)] transition-colors"
                >
                  {s.node}
                </a>
              ))}
            </div>
          </div>

          {/* Product */}
          <div className="col-span-6 sm:col-span-4 lg:col-span-2">
            <h4 className={colHeadCls}>Product</h4>
            <ul className="space-y-3">
              <li><Link to="/designer" className={linkCls}>Designer Tool</Link></li>
              <li><Link to="/pricing" className={linkCls}>Pricing</Link></li>
              <li><Link to="/projects" className={linkCls}>My Projects</Link></li>
              <li><Link to="/blog" className={linkCls}>Blog</Link></li>
              <li><Link to="/explore" className={linkCls}>Explore</Link></li>
              <li><Link to="/styles" className={linkCls}>Design Styles</Link></li>
              <li><Link to="/rooms" className={linkCls}>Room Types</Link></li>
            </ul>
          </div>

          {/* By Region + By Space */}
          <div className="col-span-6 sm:col-span-4 lg:col-span-3">
            <h4 className={colHeadCls}>By Region</h4>
            <ul className="space-y-3">
              <li><Link to="/us" className={linkCls}>USA</Link></li>
              <li><Link to="/uk" className={linkCls}>United Kingdom</Link></li>
              <li><Link to="/eu" className={linkCls}>Europe</Link></li>
              <li><Link to="/italy" className={linkCls}>Italia</Link></li>
              <li><Link to="/france" className={linkCls}>France</Link></li>
            </ul>

            <h4 className={`${colHeadCls} mt-8 sm:mt-10`}>By Space</h4>
            <ul className="space-y-3">
              <li><Link to="/apartment-makeover" className={linkCls}>Apartment Makeover</Link></li>
              <li><Link to="/living-room-design" className={linkCls}>Living Room Design</Link></li>
              <li><Link to="/bedroom-design" className={linkCls}>Bedroom Design</Link></li>
              <li><Link to="/ai-interior-design-app" className={linkCls}>AI Interior Design App</Link></li>
            </ul>
          </div>

          {/* Legal + Contact */}
          <div className="col-span-12 sm:col-span-4 lg:col-span-3">
            <h4 className={colHeadCls}>Legal</h4>
            <ul className="space-y-3">
              <li><Link to="/privacy" className={linkCls}>Privacy Policy</Link></li>
              <li><Link to="/terms" className={linkCls}>Terms of Service</Link></li>
              <li><Link to="/cookie-policy" className={linkCls}>Cookie Policy</Link></li>
              <li><Link to="/cookie-settings" className={linkCls}>Cookie Settings</Link></li>
              {showCCPA && (
                <li><Link to="/ccpa-notice" className={linkCls}>Do Not Sell My Information</Link></li>
              )}
            </ul>

            <h4 className={`${colHeadCls} mt-8 sm:mt-10`}>Contact</h4>
            <p className="text-[13px] leading-[1.6] text-[#555] mb-2">
              For privacy requests or inquiries:
            </p>
            <a
              href="mailto:hello.dc@atheniaai.com"
              className="text-[14px] text-[#111] ed-underline break-all"
            >
              hello.dc@atheniaai.com
            </a>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-[#e8e8e8] flex flex-wrap items-center justify-between gap-3 text-[12px] text-[#888]">
          <p>© {new Date().getFullYear()} Decor Canvas. All rights reserved.</p>
          <p className="tracking-[0.14em] uppercase">{"\n"}</p>
        </div>
      </div>
    </footer>
  );
};
